import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { nb } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../contexts/DataContext';
import { useEmail } from '../contexts/EmailContext';

const { FiCalendar, FiClock, FiUser, FiCheckCircle, FiChevronLeft, FiChevronRight, FiVideo, FiInfo, FiCopy, FiPhone, FiAlertCircle, FiMonitor, FiCast, FiUsers } = FiIcons;

const BookingWidget = ({ onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addBooking, sellers = [], bookings = [] } = useData();
  const { sendBookingNotification = () => {} } = useEmail();

  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    meetingPlatform: 'google_meet' 
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');

  const queryParams = new URLSearchParams(location.search);
  const preSelectedSellerId = queryParams.get('sellerId');
  const [assignedSeller, setAssignedSeller] = useState(null);

  useEffect(() => {
    if (preSelectedSellerId && sellers.length > 0) {
      const found = sellers.find(s => s.id === preSelectedSellerId);
      if (found) setAssignedSeller(found);
    }
  }, [preSelectedSellerId, sellers]);

  const meetingPlatforms = [
    { id: 'google_meet', label: 'Google Meet', icon: FiVideo, color: 'text-blue-500' },
    { id: 'teams', label: 'MS Teams', icon: FiUsers, color: 'text-indigo-500' },
    { id: 'zoom', label: 'Zoom', icon: FiMonitor, color: 'text-blue-600' }
  ];

  const service = {
    name: "Plattform-demo (Video)",
    duration: "30 min",
    ingress: "En personlig gjennomgang av HMS Varsling med en av våre eksperter.",
    details: "Vi går gjennom hvordan systemet fungerer, viser varslingsmodulen for ansatte, og demonstrerer saksbehandlingen i admin-panelet. Vi svarer også på dine spørsmål om lovverk og GDPR. Møtet er helt uforpliktende."
  };

  const timeSlots = ["09:00", "09:30", "10:00", "10:30", "11:00", "13:00", "13:30", "14:00", "14:30", "15:00"];
  const weekDays = [...Array(5)].map((_, i) => addDays(currentWeekStart, i));

  const isSlotAvailable = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    const isTaken = bookings?.some(b => {
      const bDate = new Date(b.date).toISOString().split('T')[0];
      return bDate === dateStr && b.time === time && b.status !== 'cancelled' && (!assignedSeller || b.sellerId === assignedSeller.id);
    });
    return !isTaken;
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setError(null);
  };

  const handleTimeClick = (time) => {
    setSelectedTime(time);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      let finalSeller = assignedSeller;
      if (!finalSeller) {
        const activeSellers = sellers.filter(s => s.active);
        finalSeller = activeSellers.length > 0 ? activeSellers[Math.floor(Math.random() * activeSellers.length)] : { id: 'system', name: 'System', email: 'support@hmsvarsling.no' };
      }

      const bookingData = {
        sellerId: finalSeller.id,
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        customerCompany: formData.company,
        date: selectedDate.toISOString(),
        time: selectedTime,
        serviceName: service.name,
        notes: formData.notes,
        meetingPlatform: formData.meetingPlatform
      };

      const result = await addBooking(bookingData);
      if (!result.success) throw new Error("Kunne ikke lagre booking.");

      // Merk: Vi setter ikke lenger placeholder-lenke for å unngå 400-feil
      setGeneratedLink('');

      if (finalSeller.email) {
        await sendBookingNotification(finalSeller.email, { ...bookingData, link: 'Sendes av selger' });
      }

      setStep(3);
      if (onComplete) onComplete();
    } catch (err) {
      console.error(err);
      setError(err.message || "En feil oppstod.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-5xl mx-auto border border-gray-100 flex flex-col md:flex-row min-h-[600px]">
      {/* Sidebar Info */}
      <div className="bg-slate-900 text-white p-8 md:w-1/3 flex flex-col">
        <div className="mb-8">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
            <SafeIcon icon={FiVideo} className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Book en demo</h2>
          <p className="text-slate-400 text-sm">Se hvordan plattformen kan forenkle din hverdag.</p>
        </div>
        <div className="space-y-6 flex-1">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Tjeneste</p>
            <p className="font-bold text-lg mb-2">{service.name}</p>
            <div className="text-xs text-slate-300 bg-slate-800 p-4 rounded-lg border border-slate-700 leading-relaxed">
              <div className="flex gap-2 mb-2 font-semibold text-white"> <SafeIcon icon={FiInfo} /> Hva går vi gjennom? </div>
              {service.details}
            </div>
          </div>
          {selectedDate && selectedTime && (
            <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-500/30 mt-auto">
              <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold mb-1">Valgt tidspunkt</p>
              <p className="font-medium text-white">{format(selectedDate, 'EEEE d. MMMM', { locale: nb })} kl. {selectedTime}</p>
              <div className="mt-3 pt-3 border-t border-blue-500/20 flex items-center gap-2">
                <SafeIcon icon={meetingPlatforms.find(p => p.id === formData.meetingPlatform)?.icon} className="text-blue-400" />
                <span className="text-xs font-bold text-blue-100">{meetingPlatforms.find(p => p.id === formData.meetingPlatform)?.label}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 bg-white relative">
        <AnimatePresence mode='wait'>
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="h-full flex flex-col" >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Velg tidspunkt</h3>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon icon={FiChevronLeft} /></button>
                  <span className="text-sm font-medium py-2">{format(currentWeekStart, 'MMMM yyyy', { locale: nb })}</span>
                  <button onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon icon={FiChevronRight} /></button>
                </div>
              </div>
              <div className="grid grid-cols-5 gap-2 mb-6 text-center">
                {weekDays.map((day, i) => (
                  <button key={i} onClick={() => handleDateClick(day)} className={`p-2 rounded-lg text-sm transition-all ${isSameDay(day, selectedDate) ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-gray-50 text-gray-700'}`} >
                    <span className="block text-xs opacity-70 uppercase">{format(day, 'EEE', { locale: nb })}</span>
                    <span className="block font-bold text-lg">{format(day, 'd')}</span>
                  </button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto">
                <h4 className="text-sm font-semibold text-gray-500 mb-3">Ledige timer</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {timeSlots.map(time => {
                    const available = isSlotAvailable(selectedDate, time);
                    return (
                      <button key={time} onClick={() => available && handleTimeClick(time)} disabled={!available} className={`py-2 px-3 rounded-lg text-sm border transition-all ${selectedTime === time ? 'border-blue-600 bg-blue-50 text-blue-700 font-bold ring-2 ring-blue-200' : available ? 'border-gray-200 hover:border-blue-400 text-gray-700' : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed line-through'}`} >
                        {time}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-6 pt-6 border-t flex justify-end">
                <button onClick={() => setStep(2)} disabled={!selectedTime} className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium disabled:opacity-50 hover:bg-blue-700 flex items-center gap-2"> Neste steg <SafeIcon icon={FiChevronRight} /> </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="h-full flex flex-col" >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Dine opplysninger</h3>
              <form onSubmit={handleSubmit} className="space-y-4 flex-1 overflow-y-auto pr-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1 ml-1">Navn *</label>
                    <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Ola Nordmann" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1 ml-1">Bedrift</label>
                    <input type="text" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Firma AS" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1 ml-1">E-post *</label>
                    <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="ola@bedrift.no" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1 ml-1">Telefon *</label>
                    <input type="tel" required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="900 00 000" />
                  </div>
                </div>

                {/* PLATTFORM VALG */}
                <div className="pt-4 border-t border-gray-100">
                  <label className="block text-[10px] font-black uppercase text-gray-400 mb-3 ml-1">Ønsket Møteplattform</label>
                  <div className="grid grid-cols-3 gap-3">
                    {meetingPlatforms.map(platform => (
                      <button
                        key={platform.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, meetingPlatform: platform.id })}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.meetingPlatform === platform.id ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                      >
                        <SafeIcon icon={platform.icon} className={`text-xl ${platform.color}`} />
                        <span className="text-[10px] font-bold text-gray-700">{platform.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase text-gray-400 mb-1 ml-1">Melding (valgfritt)</label>
                  <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} className="w-full px-4 py-2 border rounded-lg" rows="2" placeholder="Er det noe spesielt du vil se?" />
                </div>
                <div className="mt-6 pt-6 border-t flex justify-between items-center">
                  <button type="button" onClick={() => setStep(1)} className="text-gray-500 font-bold text-sm">Tilbake</button>
                  <button type="submit" disabled={isSubmitting} className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg flex items-center gap-2">
                    {isSubmitting ? 'Bekrefter...' : 'Fullfør Booking'} <SafeIcon icon={FiCheckCircle} />
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="h-full flex flex-col items-center justify-center text-center p-8" >
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6"> <SafeIcon icon={FiCheckCircle} className="text-4xl" /> </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">Booking Bekreftet!</h2>
              <p className="text-gray-600 mb-8 max-w-md">Vi har sendt en foreløpig bekreftelse til <strong>{formData.email}</strong>.</p>
              
              <div className="bg-slate-50 p-6 rounded-2xl border border-gray-100 w-full max-w-md mb-8 text-left">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={meetingPlatforms.find(p => p.id === formData.meetingPlatform)?.icon} className="text-blue-600" />
                    <span className="font-bold text-gray-800 text-sm">{meetingPlatforms.find(p => p.id === formData.meetingPlatform)?.label}</span>
                  </div>
                  <span className="text-xs font-bold text-blue-600 uppercase">{selectedTime}</span>
                </div>
                
                {generatedLink && !generatedLink.includes('demo') ? (
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-3">
                    <code className="text-[10px] text-blue-600 truncate flex-1 font-mono">{generatedLink}</code>
                    <button onClick={() => navigator.clipboard.writeText(generatedLink)} className="text-gray-400 hover:text-blue-600"> <SafeIcon icon={FiCopy} /> </button>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                    <SafeIcon icon={FiAlertCircle} className="text-blue-600 mt-0.5" />
                    <p className="text-xs text-blue-800 font-medium leading-relaxed">
                      Møtelenke sendes i bekreftelsen fra selger.
                    </p>
                  </div>
                )}
              </div>
              <button onClick={() => navigate('/')} className="text-blue-600 font-bold uppercase text-xs tracking-widest hover:underline">Gå til forsiden</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingWidget;